import React from "react";
import { Link } from "react-router-dom";

interface FooterProps {
  data: any;
}

export const Footer: React.FC<FooterProps> = ({ data }) => {
  const {
    appName,
    discordUrl,
    footerText = "O servidor regido pelo Código Aberto.",
    sponsorLogoUrl,
    sponsorName,
  } = data && data.siteConfig ? data.siteConfig : ({} as any);

  return (
    <footer className="bg-[#020617] border-t border-white/5 pt-16 pb-0 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 md:gap-8 mb-16">
        <div className="col-span-1 sm:col-span-2 md:col-span-2">
          <h3 className="font-heading font-bold text-2xl text-white mb-6 flex items-center gap-4 tracking-tight">
            <div className="w-12 h-12 rounded-xl border border-white/10 overflow-hidden shadow-xl">
              <img
                src="/logo_main.png"
                alt="Games no Linux"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            {appName}
          </h3>
          <p className="text-slate-400 max-w-sm mb-8 leading-relaxed text-sm">
            {footerText}
          </p>
          <div className="text-xs text-slate-600 font-medium">
            © {new Date().getFullYear()} Games no Linux. <br />
            Não afiliado à Hypixel Studios ou Riot Games.
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-6 uppercase tracking-wider text-xs border-b border-[#14B8A6]/30 pb-2 inline-block">
            Explorar
          </h4>
          <ul className="space-y-3 text-slate-400 text-sm">
            <li>
              <Link
                to="/map"
                className="hover:text-[#14B8A6] transition-colors flex items-center gap-2 hover:translate-x-1 duration-200"
              >
                Mapa Mundi
              </Link>
            </li>
            <li>
              <Link
                to="/map"
                className="hover:text-[#14B8A6] transition-colors flex items-center gap-2 hover:translate-x-1 duration-200"
              >
                Atlas de Regiões
              </Link>
            </li>
            <li>
              <Link
                to="/mods"
                className="hover:text-[#14B8A6] transition-colors flex items-center gap-2 hover:translate-x-1 duration-200"
              >
                Mods & Sistemas
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-white mb-6 uppercase tracking-wider text-xs border-b border-[#14B8A6]/30 pb-2 inline-block">
            Comunidade
          </h4>
          <ul className="space-y-3 text-slate-400 text-sm">
            <li>
              <Link
                to="/players"
                className="hover:text-[#14B8A6] transition-colors flex items-center gap-2 hover:translate-x-1 duration-200"
              >
                Ranking de Jogadores
              </Link>
            </li>
            <li>
              <Link
                to="/rules"
                className="hover:text-[#14B8A6] transition-colors flex items-center gap-2 hover:translate-x-1 duration-200"
              >
                Regras do Servidor
              </Link>
            </li>
            <li>
              <a
                href={discordUrl}
                target="_blank"
                rel="noreferrer"
                className="hover:text-[#14B8A6] transition-colors flex items-center gap-2 hover:translate-x-1 duration-200"
              >
                Servidor Discord
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Sponsors Full Width Marquee */}
      {(() => {
        const sponsorsList = [...(data?.sponsors || [])].filter(
          (s) => s.isActive
        );
        if (sponsorsList.length === 0 && sponsorName) {
          sponsorsList.push({
            id: "legacy",
            name: sponsorName || "Patrocinador",
            url: "/sponsors",
            logoUrl: sponsorLogoUrl || "/logo-patrocinio.jpeg",
            isActive: true,
          } as any);
        }

        if (sponsorsList.length === 0) return null;

        // Render multiple times to ensure it spans the whole horizontal width on ultrawide monitors
        const repeatedSponsors = Array(20).fill(sponsorsList).flat();

        return (
          <div className="w-full border-t border-white/5 pt-10 mt-6 relative group">
            <div className="max-w-7xl mx-auto flex items-center justify-between px-6 mb-8">
              <h4 className="font-bold text-white uppercase tracking-wider text-xs border-b border-amber-500/30 pb-2 inline-block">
                Nossos Patrocinadores
              </h4>
              <Link
                to="/sponsors"
                className="text-[10px] font-bold text-amber-500 hover:text-amber-400 uppercase tracking-widest transition-colors flex items-center gap-1"
              >
                Ver Todos →
              </Link>
            </div>

            <div className="w-full overflow-hidden relative">
              {/* Edge Gradient Masks for smooth entry/exit */}
              <div className="absolute inset-y-0 left-0 w-16 md:w-48 bg-gradient-to-r from-[#020617] to-transparent z-10 pointer-events-none"></div>
              <div className="absolute inset-y-0 right-0 w-16 md:w-48 bg-gradient-to-l from-[#020617] to-transparent z-10 pointer-events-none"></div>

              <div className="animate-marquee flex gap-6 md:gap-10 py-4 items-center w-max">
                {repeatedSponsors.map((sp, idx) => (
                  <Link
                    key={`${sp.id}-${idx}`}
                    to={`/sponsors#${sp.id}`}
                    title={sp.name}
                    className="shrink-0 w-32 md:w-48 h-16 md:h-20 flex items-center justify-center p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/40 hover:shadow-[0_0_20px_rgba(245,158,11,0.15)] transition-all duration-300 group/item"
                  >
                    <img
                      src={sp.logoUrl || "/logo-patrocinio.jpeg"}
                      alt={sp.name || "Patrocinador"}
                      className="max-w-full max-h-[80%] object-contain filter grayscale group-hover/item:grayscale-0 transition-all duration-300"
                      loading="lazy"
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </footer>
  );
};
